import * as SelectPrimitive from "@radix-ui/react-select";
import { cva, VariantProps } from "class-variance-authority";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import * as React from "react";

import {
  ColorContext,
  theme,
} from "@/components/canvas-editor/components/Container";
import { preventBubble } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const triggerVariants = cva(
  "relative text-slate-normal font-normal flex items-center justify-between rounded-md px-3 py-2 ring-offset-background placeholder:text-slate-dim focus:outline-hidden focus:ring-2  focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
  {
    variants: {
      variant: {
        default: "border border-slate-normal bg-slate-subtle",
        ghost: "bg-slatea-ghost",
        action: "bg-slate-action",
      },
      size: {
        default: "h-10 text-sm",
        sm: "h-8 text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const SelectTrigger = ({
  className,
  children,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> &
  VariantProps<typeof triggerVariants>) => {
  const color = React.use(ColorContext);

  return (
    <SelectPrimitive.Trigger
      className={cn(
        "text-left",
        triggerVariants({ variant, size }),
        color && theme.input[color],
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-50 ml-2" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
};
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) => (
  <SelectPrimitive.ScrollUpButton
    {...preventBubble}
    className={cn(
      "flex cursor-default items-center justify-center py-2 pointer-events-auto",
      className,
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
);
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) => (
  <SelectPrimitive.ScrollDownButton
    {...preventBubble}
    className={cn(
      "flex cursor-default items-center justify-center py-2 pointer-events-auto",
      className,
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
);
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = ({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      className={cn(
        "relative z-50 max-h-96 min-w-(--radix-select-trigger-width) overflow-hidden rounded-md border border-slate-dim bg-slate-subtle text-slate-dim shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-(--radix-select-trigger-height) w-fit min-w-(--radix-select-trigger-width)",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) => (
  <SelectPrimitive.Label
    className={cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className)}
    {...props}
  />
);
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) => (
  <SelectPrimitive.Item
    className={cn(
      "group relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-hidden focus:bg-slate-4 dark:focus:bg-slatedark-6 focus:text-slate-normal data-disabled:pointer-events-none data-disabled:opacity-50",
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
);
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) => (
  <SelectPrimitive.Separator
    className={cn("-mx-1 my-1 h-px bg-slate-6 dark:bg-slatedark-6", className)}
    {...props}
  />
);
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export interface SimpleSelectProps {
  defaultValue?: string;
  value?: string;
  placeholder?: string;
  onValueChange?: (v: string) => void;
  options:
    | string[]
    | readonly string[]
    | Array<{ value: string; label: React.ReactNode }>;
  className?: string;
  tooltip?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  required?: boolean;
  ref?: React.RefObject<HTMLButtonElement>;
  onPointerDown?: (e: React.PointerEvent<HTMLButtonElement>) => void;
  onTouchStart?: (e: React.TouchEvent<HTMLButtonElement>) => void;
  name?: string;
  id?: string;
  "aria-label": string;
  size?: "sm" | "default";
}

const SimpleSelect = function SimpleSelect({
  ref,
  options,
  value,
  defaultValue,
  placeholder,
  onValueChange,
  className,
  tooltip,
  open,
  onOpenChange,
  required,
  onPointerDown,
  onTouchStart,
  name,
  id,
  size,
  ...props
}: SimpleSelectProps) {
  const trigger = (
    <SelectTrigger
      ref={ref}
      className={cn(className, "shrink-0")}
      {...preventBubble}
      onPointerDown={onPointerDown || preventBubble.onPointerDown}
      onTouchStart={onTouchStart || preventBubble.onTouchStart}
      id={id}
      aria-label={props["aria-label"]}
      size={size}
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
  );

  return (
    <Select
      name={name}
      required={required}
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
      open={open}
      onOpenChange={onOpenChange}
    >
      {tooltip ? (
        <SimpleTooltip message={tooltip} asChild>
          {trigger}
        </SimpleTooltip>
      ) : (
        trigger
      )}
      <SelectContent {...preventBubble}>
        {options.map((match) => {
          const key = typeof match === "string" ? match : match.value;
          const option =
            typeof match === "string" ? (
              <SelectItem key={key} value={match}>
                {match}
              </SelectItem>
            ) : (
              <SelectItem key={key} value={match.value}>
                {match.label}
              </SelectItem>
            );

          return option;
        })}
      </SelectContent>
    </Select>
  );
};

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SimpleSelect,
};
